import { useState, type FC, type MouseEvent } from 'react';

import { isNextAuthEnabled } from '@eneco/auth';
import { Placeholder, usePlaceholder, useRendering } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { LoginButton } from '@eneco/components-next/auth';
import { Link as NextLink } from '@eneco/next';
import { DesktopHeader, MobileHeader, NavLink, PageGrid, StickyHeader } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ProfileIcon } from '@eneco/sparky/icons';
import Cookies from 'js-cookie';

import type { LoginMenuContent } from './login-menu-content.types';
import { MAIN_NAVIGATION_SLOTS, resolveSlot } from './slots';
import type { TopMenuContent } from './top-menu-content.types';

const CUSTOMER_INSIGHTS_VISITOR_ID_COOKIE = '_msci';
const CUSTOMER_INSIGHTS_SEARCH_PARAM = 'visitor_id';

/**
 * `MainNavigation` block for the CMS-agnostic lane, serving **both** providers. Carries no fields of its
 * own on either provider — purely a structural container for its named regions (`logo`, `left`/`right`
 * navigation, …), same role as `Section`/`SplitView`.
 *
 * Built on the design system's own `StickyHeader`/`DesktopHeader`/`MobileHeader`, which is what the
 * production header app composes too (`apps/header/shared/multilabel/src/Header.tsx`). That app itself
 * is tagged `app:next` and outside this lib's module boundary (`nx.json`), but the Sparky components it
 * is built from are not — so the chrome here is the real one: sticky bar, hamburger-left on mobile with
 * the navigation in a dialog, hover dropdowns on desktop.
 *
 * Two deliberate differences from production, both because this lane has no profile menu and no header
 * sub-apps yet:
 *
 * - **The mobile profile button is inert.** `MobileHeader` always renders it, and there is no profile
 *   menu to open here. Its handler is a no-op rather than a dialog onto nothing. Legacy also hands it the
 *   login menu's link; that would add a second profile button inside the open hamburger menu, which opens
 *   the profile menu in legacy and could only do nothing here, so it is left out until the profile menu exists.
 * - **No `SideNav` or `SecondaryMenu` sub-apps.** Those regions render whatever the CMS puts in the
 *   corresponding placeholder, or nothing.
 *
 * The top header and right-hand slots are read as legacy's header reads them: the links of the `TopMenu`
 * rendering go into the desktop header's top row and the mobile menu's top
 * (`apps/header/shared/multilabel/src/TopMenu.tsx`), and the `LoginMenu` rendering becomes the desktop
 * login entry (`LoginMenu.tsx`). Logged in, legacy shows a profile menu there instead; this lane does not
 * have one yet, so a logged-in visitor gets no entry with NextAuth on.
 */
const MainNavigation: FC = () => {
  const rendering = useRendering();
  const isDesktop = useMediaQuery('lg');
  const [isHamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);

  const logoSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.logo);
  const leftSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.left);
  const rightSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.right);
  const topheaderSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.topheader);
  const secondarySlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.secondary);
  const sideSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.side);
  const breadcrumbsSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.breadcrumbs);
  const headerLangSlot = resolveSlot(rendering, MAIN_NAVIGATION_SLOTS.headerLang);

  const topMenu = usePlaceholder(topheaderSlot ?? MAIN_NAVIGATION_SLOTS.topheader[0]).find(
    child => child.componentName === 'TopMenu',
  );
  const topMenuItems = topMenuLinks(topMenu?.content as TopMenuContent | undefined);
  const loginMenu = usePlaceholder(rightSlot ?? MAIN_NAVIGATION_SLOTS.right[0]).find(
    child => child.componentName === 'LoginMenu',
  );
  const login = loginMenuFields(loginMenu?.content as LoginMenuContent | undefined);

  const logo = logoSlot ? <Placeholder name={logoSlot} /> : null;
  const navigation = leftSlot ? <Placeholder name={leftSlot} /> : null;
  const languageSelector = <Placeholder name={headerLangSlot ?? MAIN_NAVIGATION_SLOTS.headerLang[0]} />;

  return (
    <>
      <StickyHeader>
        {isDesktop ? (
          <DesktopHeader>
            {topMenuItems && <DesktopHeader.Top>{topMenuItems}</DesktopHeader.Top>}
            <DesktopHeader.Main
              logoLink={logo}
              // As legacy's non-dropdown header, always the language placeholder, even when empty: Sparky
              // then labels the nav "Taal en profiel" rather than "Profielnavigatie".
              languageSelector={languageSelector}
              profileLink={login ? <LoginEntry {...login} /> : null}
              // Mirrors `apps/header/shared/multilabel/src/Header.tsx`'s `isOpenDomain` (no secondary nav
              // → open domain). Required, not cosmetic: `styledMainRecipe` has no default variant, so
              // leaving it undefined renders the header with no background at all rather than white.
              isOpenDomain={!secondarySlot}>
              {navigation}
            </DesktopHeader.Main>
            {secondarySlot && <Placeholder name={secondarySlot} />}
          </DesktopHeader>
        ) : (
          <MobileHeader
            isHamburgerMenuOpen={isHamburgerMenuOpen}
            setHamburgerMenuOpen={setHamburgerMenuOpen}
            // No `hamburgerMenuLabel`: Sparky renders it as a visible caption under the icon, not as an
            // accessible name, and production passes it only where the CMS supplies a menu title.
            // Giving it one put "Main navigation" on screen next to the burger.
            // This lane has no profile menu behind this button yet. See the doc comment.
            isProfileMenuOpen={false}
            setProfileMenuOpen={noop}
            logoLink={logo}>
            <MobileHeader.HamburgerMenu isOpen={isHamburgerMenuOpen}>
              {topMenuItems && <MobileHeader.Top>{topMenuItems}</MobileHeader.Top>}
              <MobileHeader.Main>{navigation}</MobileHeader.Main>
              {/* As legacy, always rendered: even with an empty language slot it closes the menu with a divider. */}
              <MobileHeader.Bottom>
                {languageSelector}
                {sideSlot && <Placeholder name={sideSlot} />}
              </MobileHeader.Bottom>
            </MobileHeader.HamburgerMenu>
          </MobileHeader>
        )}
      </StickyHeader>
      {breadcrumbsSlot && (
        <PageGrid>
          <PageGrid.Item gridColumn="1/-1">
            <Placeholder name={breadcrumbsSlot} />
          </PageGrid.Item>
        </PageGrid>
      )}
    </>
  );
};

const noop = () => undefined;

/** The `LoginMenu` rendering's fields, on the block (Sitecore) or its site-wide item (Sanity); none without a link. */
const loginMenuFields = (content: LoginMenuContent | undefined) => {
  const fields = content?.selfServiceLink ? content : content?.siteWideContent;
  const link = linkWithUrl(fields?.selfServiceLink);
  return link
    ? {
        link,
        sendLocaleToOkta: !!fields?.sendLocaleToOkta,
        sendVisitorId: !!fields?.sendCustomerInsightsVisitorIdFlag,
      }
    : null;
};

/** As legacy's `LoginMenu`: NextAuth's sign-in button when NextAuth is on, otherwise a link to the self-service portal. */
const LoginEntry: FC<NonNullable<ReturnType<typeof loginMenuFields>>> = ({ link, sendLocaleToOkta, sendVisitorId }) => {
  if (isNextAuthEnabled) {
    return (
      <LoginButton leftIcon={<ProfileIcon />} label={link.text} callbackUrl={link.href} sendLocale={sendLocaleToOkta} />
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!sendVisitorId) return;

    // To match an anonymous visitor with a customer profile, the self-service portal gets the visitor id.
    let url: URL;
    try {
      url = new URL(link.href);
    } catch {
      return;
    }
    const visitorId = Cookies.get(CUSTOMER_INSIGHTS_VISITOR_ID_COOKIE);
    if (visitorId) url.searchParams.set(CUSTOMER_INSIGHTS_SEARCH_PARAM, visitorId);
    (event.currentTarget as HTMLAnchorElement).href = url.toString();
  };

  // Behind `@eneco/next`'s `Link`, as legacy wraps it: client-side routing, and an internal link keeps `?_cms=`.
  return (
    <NextLink href={link.href} linkType={link.linkType ?? ''}>
      <NavLink leftIcon={<ProfileIcon />} onClick={handleClick}>
        {link.text}
      </NavLink>
    </NextLink>
  );
};

/**
 * As legacy's `TopMenu`: nothing without `items`, otherwise a tertiary `NavLink` per item that has a destination,
 * behind `@eneco/next`'s `Link` as legacy wraps it. Legacy passes only `href` and `linktype`, so no `target`.
 */
const topMenuLinks = (content: TopMenuContent | undefined) => {
  const items: TopMenuContent['items'] = content?.items ?? content?.siteWideContent?.items;
  if (!items) return null;

  return items.flatMap((item, index) => {
    const link = linkWithUrl(item.link);
    return link
      ? [
          <li key={item._key ?? item.id ?? String(index)}>
            <NextLink href={link.href} linkType={link.linkType ?? ''}>
              <NavLink variant="tertiary">{link.text}</NavLink>
            </NextLink>
          </li>,
        ]
      : [];
  });
};

export default MainNavigation;
